import {
  DEFAULT_DRAFT_LEAD_FILTER,
  DEFAULT_TEST_LEAD_FILTER,
  DraftLeadFilter,
  LeadGroupByKey,
  TestLeadFilter,
} from './leadsAdmin';

export type LeadsAdminTabKey = 'all' | 'food_entrepreneur' | 'resource_provider' | 'groups';

export type PersistedLeadsAdminFilters = {
  tab: LeadsAdminTabKey;
  searchInput: string;
  filterStage: string;
  filterStatus: string;
  filterState: string;
  draftLeadFilter: DraftLeadFilter;
  testLeadFilter: TestLeadFilter;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
  groupBy: LeadGroupByKey;
  showFilters: boolean;
};

const STORAGE_VERSION = 'v1';
const STORAGE_PREFIX = `homecook_leads_admin_filters_${STORAGE_VERSION}`;

const DEFAULT_PERSISTED: PersistedLeadsAdminFilters = {
  tab: 'all',
  searchInput: '',
  filterStage: '',
  filterStatus: '',
  filterState: '',
  draftLeadFilter: DEFAULT_DRAFT_LEAD_FILTER,
  testLeadFilter: DEFAULT_TEST_LEAD_FILTER,
  pageSize: 10,
  sort: 'created',
  dir: 'desc',
  groupBy: 'none',
  showFilters: false,
};

const isTabKey = (value: unknown): value is LeadsAdminTabKey =>
  value === 'all' ||
  value === 'food_entrepreneur' ||
  value === 'resource_provider' ||
  value === 'groups';

const isDraftFilter = (value: unknown): value is DraftLeadFilter =>
  value === 'include' || value === 'only' || value === 'exclude';

const isTestFilter = (value: unknown): value is TestLeadFilter =>
  value === 'include' || value === 'only' || value === 'exclude';

const isGroupByKey = (value: unknown): value is LeadGroupByKey =>
  value === 'none' ||
  value === 'lead_stage' ||
  value === 'lead_status' ||
  value === 'lead_category' ||
  value === 'state' ||
  value === 'city';

/** Build a versioned localStorage key scoped to the signed-in admin email. */
export const leadsAdminFilterStorageKey = (adminEmail?: string): string =>
  `${STORAGE_PREFIX}:${(adminEmail ?? 'anonymous').trim().toLowerCase()}`;

/** Serialize CRM table filter settings for localStorage. */
export const serializeLeadsAdminFilters = (
  filters: PersistedLeadsAdminFilters
): string => JSON.stringify(filters);

/** Parse persisted CRM filter settings, falling back to defaults on invalid data. */
export const parseLeadsAdminFilters = (raw: string | null): PersistedLeadsAdminFilters => {
  if (!raw) return { ...DEFAULT_PERSISTED };
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedLeadsAdminFilters>;
    return {
      tab: isTabKey(parsed.tab) ? parsed.tab : DEFAULT_PERSISTED.tab,
      searchInput: typeof parsed.searchInput === 'string' ? parsed.searchInput : '',
      filterStage: typeof parsed.filterStage === 'string' ? parsed.filterStage : '',
      filterStatus: typeof parsed.filterStatus === 'string' ? parsed.filterStatus : '',
      filterState: typeof parsed.filterState === 'string' ? parsed.filterState : '',
      draftLeadFilter: isDraftFilter(parsed.draftLeadFilter)
        ? parsed.draftLeadFilter
        : DEFAULT_DRAFT_LEAD_FILTER,
      testLeadFilter: isTestFilter(parsed.testLeadFilter)
        ? parsed.testLeadFilter
        : DEFAULT_TEST_LEAD_FILTER,
      pageSize: typeof parsed.pageSize === 'number' && parsed.pageSize > 0 ? parsed.pageSize : 10,
      sort: typeof parsed.sort === 'string' ? parsed.sort : 'created',
      dir: parsed.dir === 'asc' || parsed.dir === 'desc' ? parsed.dir : 'desc',
      groupBy: isGroupByKey(parsed.groupBy) ? parsed.groupBy : 'none',
      showFilters: Boolean(parsed.showFilters),
    };
  } catch {
    return { ...DEFAULT_PERSISTED };
  }
};

/** Read persisted CRM filters from localStorage (browser only). */
export const loadLeadsAdminFilters = (adminEmail?: string): PersistedLeadsAdminFilters => {
  if (typeof window === 'undefined') return { ...DEFAULT_PERSISTED };
  return parseLeadsAdminFilters(window.localStorage.getItem(leadsAdminFilterStorageKey(adminEmail)));
};

/** Write persisted CRM filters to localStorage (browser only). */
export const saveLeadsAdminFilters = (
  adminEmail: string | undefined,
  filters: PersistedLeadsAdminFilters
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(leadsAdminFilterStorageKey(adminEmail), serializeLeadsAdminFilters(filters));
};
